/**
 * The survey domain (BE-141, 2026-10-12; definitions v2 BE-152, 2026-10-18) — one door for
 * every survey definition and ONE response per (user, survey) for the profile surveys.
 * Bodies and responses pass through the case interceptor: `answers` is keyed by QUESTION
 * ID, and the ids are the config's own snake_case words (`work_dress_code`), which the
 * camel → snake transform leaves as they are — the same way the capsule survey has always
 * sent them. The localized texts are `{en, ru}` objects; the client picks its language.
 */

import { apiClient } from '@/api/api'
import type { SurveyStep } from '@/features/style-space/api/types'

export type SurveyKind = 'sizes' | 'capsule' | 'lifestyle' | 'style' | 'gift' | 'outfit'

export const LIFESTYLE_SURVEY_KEY = 'default-lifestyle-survey'
export const STYLE_SURVEY_KEY = 'default-style-survey'

/** `{en, ru}` — the server's texts; `localizedText()` picks the app's language. */
export type LocalizedText = Record<string, string>

export function localizedText(text: LocalizedText | null | undefined, lang: string): string {
  if (!text) return ''
  return text[lang] ?? text.en ?? Object.values(text)[0] ?? ''
}

export type SurveyV2QuestionType = 'single' | 'multi' | 'scale' | 'text' | 'photos' | 'measurement'

export interface SurveyV2Option {
  key: string
  label: LocalizedText
  subtitle?: LocalizedText | null
  icon?: string | null
}

/** Shown only when the named earlier question has one of these keys chosen. */
export interface SurveyV2ShowIf {
  questionId: string
  optionKeys: string[]
}

export interface SurveyV2Scale {
  min: number
  max: number
  step?: number
  minLabel?: LocalizedText | null
  maxLabel?: LocalizedText | null
}

export interface SurveyV2Question {
  id: string
  type: SurveyV2QuestionType
  title: LocalizedText
  subtitle?: LocalizedText | null
  hint?: LocalizedText | null
  required?: boolean
  /** single / multi: `{"key": "other", "text": "…"}` is accepted. */
  allowCustom?: boolean
  customLabel?: LocalizedText | null
  placeholder?: LocalizedText | null
  maxLength?: number
  options?: SurveyV2Option[]
  scale?: SurveyV2Scale | null
  showIf?: SurveyV2ShowIf | null
  icon?: string | null
}

export interface SurveyV2Step {
  id: string
  title: LocalizedText
  subtitle?: LocalizedText | null
  questions: SurveyV2Question[]
  showIf?: SurveyV2ShowIf | null
}

/** § 2.1 schema — the lifestyle (v2), style, gift and outfit definitions. */
export interface SurveyV2Config {
  title: LocalizedText
  subtitle?: LocalizedText | null
  /** Free settings the flow reads (`cards_per_style`, `rate_scale`, … for the style survey). */
  settings: Record<string, unknown>
  steps: SurveyV2Step[]
}

/** The style survey's `settings` (BE-151), camelised by the interceptor. */
export interface StyleSurveySettings {
  cardsPerStyle?: number
  minStyles?: number
  rateScale?: {
    min: number
    max: number
    steps: Array<{ value: number; label: LocalizedText }>
  }
  estimatedMinutes?: number
}

/** The capsule-survey step format (`sizes` / `capsule` keep it). */
export interface SurveyLegacyConfig {
  id: string
  title: string
  steps: SurveyStep[]
}

/** `GET /surveys/{key}/config` — the published definition (`ETag` / `Cache-Control` on the wire). */
export interface SurveyDefinition<Config = SurveyV2Config | SurveyLegacyConfig> {
  key: string
  kind: SurveyKind
  version: number
  title: string
  config: Config
  publishedAt?: string | null
  etag?: string | null
}

/**
 * One answer of `PUT /surveys/{key}/response` — the canonical shape (`option_keys` /
 * `custom_text` / `value`), the "Other" shorthand, the capsule-survey shape or a bare key.
 */
export type SurveyResponseAnswer =
  | string
  | string[]
  | number
  | { optionKeys: string[]; customText?: string | null; value?: number | null }
  | { customText: string }
  | { key: 'other'; text: string }
  | {
      value: string | string[] | null
      isCustom?: boolean
      multiAnswer?: boolean
      customText?: string | null
    }

export interface SurveyResponseBody {
  answers: Record<string, SurveyResponseAnswer>
  /** the closing step; at most 2000 characters */
  freeText?: string | null
}

export interface SurveyResponseView {
  questionId: string
  questionLabel: string
  optionKeys: string[]
  labels: string[]
  customText?: string | null
  value?: number | null
}

export interface SurveyResponse {
  surveyKey: string
  answers: Record<string, unknown>
  freeText: string | null
  updatedAt: string | null
  definitionVersion?: number
  views?: SurveyResponseView[]
}

/** One row of a 422 `detail[]` — `loc` is `["body", "answers", <question_id>]` for an answer. */
export interface SurveyValidationIssue {
  loc: Array<string | number>
  msg: string
  type?: string
}

export const getSurveyDefinition = <Config = SurveyV2Config | SurveyLegacyConfig>(key: string) =>
  apiClient.get<SurveyDefinition<Config>>(`/surveys/${encodeURIComponent(key)}/config`)

/** 404 before the first PUT. */
export const getSurveyResponse = (key: string) =>
  apiClient.get<SurveyResponse>(`/surveys/${encodeURIComponent(key)}/response`)

/** 409 for `sizes` / `capsule` (their own submit routes derive sizes / resolve tags); 422 on an answer the definition refuses. */
export const putSurveyResponse = (key: string, body: SurveyResponseBody) =>
  apiClient.put<SurveyResponse>(`/surveys/${encodeURIComponent(key)}/response`, body)

export const deleteSurveyResponse = (key: string) =>
  apiClient.delete<{ surveyKey: string; deleted: number }>(`/surveys/${encodeURIComponent(key)}/response`)

/**
 * The field errors of a 422 answer: question id → the server's message. Anything that is
 * not the survey envelope (a network error, a 500) gives an empty map.
 */
export function surveyFieldErrors(error: unknown): Record<string, string> {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response
  if (response?.status !== 422 || !Array.isArray(response.data?.detail)) return {}
  const errors: Record<string, string> = {}
  for (const issue of response.data.detail as SurveyValidationIssue[]) {
    const loc = Array.isArray(issue?.loc) ? issue.loc : []
    const index = loc.indexOf('answers')
    const questionId = index >= 0 ? loc[index + 1] : undefined
    if (typeof questionId === 'string' && issue.msg) errors[questionId] = issue.msg
  }
  return errors
}

/* ——— Lifestyle (BE-141, BE-152) ——— */

export interface UserLifestyleAnswer {
  questionId: string
  /** the first option (kept for older readers) */
  answerKey: string
  /** the server's label of the first option; for "Other" the typed text */
  label: string
  /** the whole multi-answer (BE-152) */
  optionKeys?: string[]
  labels?: string[]
  customText?: string | null
}

/** `GET /user/lifestyle` — the answers with their labels and the note. */
export interface UserLifestyle {
  note: string | null
  answers: UserLifestyleAnswer[]
  updatedAt: string | null
  completed: boolean
  completedAt?: string | null
  definitionVersion?: number
}

export const getUserLifestyle = () => apiClient.get<UserLifestyle>('/user/lifestyle')

/** Clears both the answers and the note; `taste.styles[]` never changes from it. */
export const deleteUserLifestyle = () => apiClient.delete<{ deleted: number }>('/user/lifestyle')

/**
 * What a read-back answer shows (BE-152): the typed "Other" text, else every chosen option's
 * server label (a multi-answer is all of them), else the first label of an older payload.
 */
export function lifestyleAnswerText(answer: UserLifestyleAnswer): string {
  const custom = answer.customText?.trim()
  if (custom) return custom
  if (answer.labels?.length) return answer.labels.join(', ')
  return answer.label
}
